import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { FolderOpen, Drop, WarningCircle, Pill, FirstAid, PhoneCall, NotePencil, Paperclip, EnvelopeSimple } from '@phosphor-icons/react';
import api from '../api/api';
import { toast } from 'sonner';

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="rounded-xl border border-border bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={16} weight="duotone" className="text-primary" />
      <p className="text-[11px] uppercase tracking-wider text-foreground/60 font-medium">{title}</p>
    </div>
    <div className="text-sm text-foreground/85 leading-relaxed">{children}</div>
  </div>
);

export const ClientRecordDialog = ({ request, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/professionals/me/requests/${request.request_id}/patient-record`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cartella non disponibile');
      setData(null);
    } finally { setLoading(false); }
  }, [request.request_id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const invite = async () => {
    setBusy(true);
    try {
      await api.post(`/api/professionals/me/requests/${request.request_id}/patient-record/invite`);
      toast.success('Invito inviato al cliente');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio invito');
    } finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!note.trim()) { toast.error('Scrivi il testo della nota'); return; }
    setBusy(true);
    try {
      await api.post(`/api/professionals/me/requests/${request.request_id}/patient-record/notes`, {
        body: note.trim(), visit_date: visitDate || null,
      });
      setNote(''); setVisitDate('');
      toast.success('Nota di visita aggiunta');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio nota');
    } finally { setBusy(false); }
  };

  const rec = data?.record;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="client-record-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen size={20} weight="duotone" className="text-primary" />
            Cartella assistenziale{data?.client_name ? ` · ${data.client_name}` : ''}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-foreground/60 py-6" data-testid="client-record-loading">Caricamento cartella…</p>
        ) : !data ? (
          <p className="text-sm text-foreground/60 py-6" data-testid="client-record-error">Impossibile caricare la cartella.</p>
        ) : !data.has_record ? (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-5 space-y-3" data-testid="client-record-missing">
            <div className="flex items-start gap-3">
              <WarningCircle size={22} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-heading font-medium">Il cliente non ha ancora una cartella</p>
                <p className="text-sm text-foreground/75 mt-1">
                  Per questa prestazione dovrai raccogliere le informazioni cliniche di persona.
                  Consigliamo di invitare il cliente a compilare la Cartella Assistenziale
                  (anamnesi, allergie, patologie croniche, farmaci, contatto di emergenza):
                  per le prossime prestazioni le troverai già pronte qui.
                </p>
              </div>
            </div>
            <Button onClick={invite} disabled={busy} className="rounded-full" data-testid="invite-record-btn">
              <EnvelopeSimple size={16} className="mr-1.5" /> Invita il cliente a compilarla
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="client-record-content">
            <p className="text-xs text-foreground/55">
              Sola lettura · aggiornata il {rec.updated_at ? new Date(rec.updated_at).toLocaleDateString('it-IT') : '—'}
            </p>

            <div className="grid sm:grid-cols-2 gap-3">
              <Section icon={Drop} title="Gruppo sanguigno" testid="record-blood">
                {rec.blood_group || <span className="text-foreground/45">Non indicato</span>}
              </Section>
              <Section icon={PhoneCall} title="Contatto di emergenza" testid="record-emergency">
                {rec.emergency_contact_name || rec.emergency_contact_phone ? (
                  <>
                    {rec.emergency_contact_name || '—'}
                    {rec.emergency_contact_phone && (
                      <> · <a className="text-primary underline" href={`tel:${rec.emergency_contact_phone}`}>{rec.emergency_contact_phone}</a></>
                    )}
                  </>
                ) : <span className="text-foreground/45">Non indicato</span>}
              </Section>
            </div>

            <Section icon={WarningCircle} title="Allergie" testid="record-allergies">
              {rec.allergies?.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {rec.allergies.map((a, i) => <Badge key={i} className="bg-red-100 text-red-800">{a}</Badge>)}
                </div>
              ) : <span className="text-foreground/45">Nessuna segnalata</span>}
            </Section>

            <Section icon={FirstAid} title="Patologie croniche" testid="record-chronic">
              {rec.chronic_conditions?.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {rec.chronic_conditions.map((c, i) => <Badge key={i} variant="outline">{c}</Badge>)}
                </div>
              ) : <span className="text-foreground/45">Nessuna segnalata</span>}
            </Section>

            <Section icon={Pill} title="Farmaci in uso" testid="record-medications">
              {rec.medications ? <p className="whitespace-pre-wrap">{rec.medications}</p> : <span className="text-foreground/45">Non indicati</span>}
            </Section>

            <Section icon={NotePencil} title="Anamnesi" testid="record-anamnesis">
              {rec.anamnesis ? <p className="whitespace-pre-wrap">{rec.anamnesis}</p> : <span className="text-foreground/45">Non compilata</span>}
            </Section>

            {rec.notes && (
              <Section icon={NotePencil} title="Note del cliente" testid="record-notes">
                <p className="whitespace-pre-wrap">{rec.notes}</p>
              </Section>
            )}

            <Section icon={Paperclip} title="Allegati" testid="record-attachments">
              {rec.attachments_count > 0
                ? <span>{rec.attachments_count} document{rec.attachments_count > 1 ? 'i' : 'o'} nella cartella · consultabili solo dal cliente</span>
                : <span className="text-foreground/45">Nessun allegato</span>}
            </Section>

            <Section icon={NotePencil} title={`Note di visita (${rec.visit_notes?.length || 0})`} testid="record-visit-notes">
              {rec.visit_notes?.length ? (
                <ul className="space-y-2">
                  {rec.visit_notes.map((n) => (
                    <li key={n.note_id} className="border-l-2 border-primary/40 pl-3">
                      <p className="text-xs text-foreground/55">
                        {n.author_name} · {n.visit_date || new Date(n.created_at).toLocaleDateString('it-IT')}
                      </p>
                      <p className="whitespace-pre-wrap">{n.body}</p>
                    </li>
                  ))}
                </ul>
              ) : <span className="text-foreground/45">Nessuna nota</span>}
            </Section>

            <div className="rounded-xl border border-primary/25 bg-primary/5 p-4 space-y-2" data-testid="record-add-note">
              <p className="text-sm font-medium">Aggiungi una nota di visita</p>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="Rilievi clinici, terapia svolta, indicazioni per la prossima visita…"
                data-testid="record-note-input"
              />
              <div className="flex flex-wrap items-center gap-2">
                <Input type="date" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} className="w-44" data-testid="record-note-date" />
                <Button onClick={addNote} disabled={busy} className="rounded-full" data-testid="record-note-save">Salva nota</Button>
              </div>
              <p className="text-[11px] text-foreground/55">Il cliente riceverà una notifica della nota aggiunta.</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClientRecordDialog;
